import {Button, Divider, Popover, PopoverContent, PopoverTrigger, Switch, useDisclosure} from "@heroui/react";
import VoiceSettingsItem from '../widgets/VoiceSettingsItem.jsx';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import {useUpdateFormValue} from '../helpers/AppHelper.jsx';
import _ from 'lodash';
import {useRecoilState} from 'recoil';
import {speakersState} from '../store.jsx';
import {useEffect, useState} from 'react';
import {useParams} from "react-router-dom";

export default function VoiceSettings({form, prevData, onValueChange, fitAudio, setFitAudio, ...props}) {
	const {isOpen, onOpen, onOpenChange, onClose} = useDisclosure();
	const queryClient = useQueryClient();
	const updateValue = useUpdateFormValue(onValueChange);
	const [speakers] = useRecoilState(speakersState)
	const [isChangesAvailable, setIsChangesAvailable] = useState(false)
	const {ID} = useParams();
	
	const addNewSpeaker = useMutation({
		mutationFn: repositories.project.addSpeaker,
		onSuccess: () => {
			toast.success('Speaker added successfully');
			queryClient.refetchQueries(['get-speakers', ID]);
			onClose();
		},
		onError: (error) => {
			toast.error(error?.response?.data?.message || 'Failed to add speaker');
		}
	})
	
	const updateSpeaker = useMutation({
		mutationFn: async () => await repositories.project.updateSpeaker(form.values?.project_id, form.values?.speakers),
		onMutate: () => form.setFieldValue('prev_speakers', prevData?.speakers),
		onSuccess: async (response, variables) => {
			toast.success('Speaker updated successfully');
			await queryClient.invalidateQueries(['get-projects', ID]);

			setTimeout(() => setIsChangesAvailable(false), 300)
			form.setFieldValue('is_change_available', true)
			onOpenChange();
		},
		onError: (error) => {
			toast.error(error?.response?.data?.message || 'An error occurred');
		}
	})
	
	useEffect(() => {
		_.forEach(form.values?.text_srt_json, (item, index) => {
			const prevSpeaker = _.find(!_.isEmpty(form.values?.prev_speakers) ? form.values?.prev_speakers : prevData?.speakers || [], (spItem) => spItem?.id == item?.speaker?.id)
			const newSpeaker = _.find(prevData?.speakers, (spItem) => spItem?.id == item?.speaker?.id)
			
			if (prevSpeaker?.voice_id != newSpeaker?.voice_id) {
				updateValue(`text_srt_json.${index}.is_audio_changed`, item?.speaker?.id == item?.speaker?.id)
			}
		})
	}, [form.values?.prev_speakers, prevData?.speakers]);
	
	useEffect(() => {
		setIsChangesAvailable(_.some(form?.values?.speakers, (item, index) => item?.voice_id != prevData?.speakers[ index ]?.voice_id));
	}, [form.values?.speakers, prevData?.speakers])
	
	return (
		<>
			<Popover
				showArrow
				classNames={{
					content: "w-72",
				}}
				isOpen={isOpen}
				shouldBlockScroll={false}
				shouldCloseOnScroll={false}
				onOpenChange={onOpenChange}
			>
				<PopoverTrigger>
					<Button
						variant="flat" radius="full" isIconOnly color="" fullWidth
					    className="bg-black/5 border-divider">
						<i className="hgi-stroke hgi-setting-07 text-xl font-bold"></i>
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className="py-4 px-4"
					aria-label="Dropdown menu with icons" variant="faded">
					<div className="flex w-full mb-3">
						<h3 className="text-left grow">Fit Translated Audio</h3>
						<Switch isSelected={fitAudio} onValueChange={setFitAudio} size="sm"/>
					</div>

					<div
						className="w-full flex justify-between items-center border-b border-default-100 pt-2 pb-3 mb-3">
						<h3 className="text-left w-full">Voice settings</h3>
						<Button
							isDisabled={!isChangesAvailable}
							isLoading={updateSpeaker.isPending}
							onPress={updateSpeaker.mutate}
							variant="flat"
							color="primary"
							size="sm">
							Apply
						</Button>
					</div>
					<div className="grid grid-cols-1 gap-3 w-full pb-2 max-h-[310px] overflow-y-auto scrollbar-hide">
						{form?.values?.speakers?.map((item, index) => (
							<Button
								key={index} fullWidth
								aria-label={item?.speaker_name}
								variant="ghost"
								className="flex items-center pr-1 !scale-100 py-6 justify-between border border-divider data-[hover=true]:!bg-primary-50/50 data-[hover=true]:border-primary"
								endContent={<VoiceSettingsItem
									form={form}
									data={item}
									index={index}
									selectedSpeaker={item?.voice_id}
									title={item?.speaker_name || 'Unknown'}
									language={form.values?.translate_language}
									onChange={updateValue}/>
								}
							>
								<span className="block w-full text-left">
									{item?.speaker_name || 'Unknown'}
								</span>
							</Button>
						))}
					</div>
					<Divider className="mb-1"/>
					<Button
						isDisabled={form?.values?.speakers?.length >= 10}
						isLoading={addNewSpeaker.isPending}
						onPress={() => addNewSpeaker.mutate({
							project_id: form.values?.project_id,
							newSpeakers: [{
								speaker_name: `Speaker ${_.size(form.values?.speakers) + 1}`,
								voice_id: _.head(speakers)?.voice_id,
							}],
						})}
						fullWidth variant="ghost"
						className="flex items-center pr-1 !scale-100 py-2 justify-between border-none data-[hover=true]:!bg-primary-50/50 data-[hover=true]:border-primary font-sans"
					>
						<i className="hgi-stroke hgi-add-01 text-xl"></i>
						<span className="block w-full text-left">
								Add new speaker
							</span>
					</Button>
				</PopoverContent>
			</Popover>
		</>
	
	);
}
